import React, { Suspense, useCallback } from 'react'
import MainLayout from '../layouts/MainLayout'
import Overview from './Overview'
import { TabStack, TabContent } from './TabStack'
import { useTabs, useActiveTab } from '../contexts/TabManagerContext'
import type { Tab } from '../hooks/multiWindow'

/**
 * Renders every open tab's content in parallel via `<TabStack>`. Consumed
 * from the `TabManagerContext` that `MainLayout` sets up — the hook is only
 * called once (inside MainLayout), so all consumers share the same tab
 * state. Inactive tabs stay mounted but hidden, making tab switches
 * instant (no remount, no list flash, preserved scroll + selection).
 */
const TabStackHost: React.FC = () => {
  const { tabs } = useTabs()
  const { activeTabId } = useActiveTab()
  // Stable render-prop so `React.memo(TabStack)` can bail when tabs/activeId
  // are unchanged. An inline arrow would create a new function reference on
  // every render and defeat the memo. `isActive` is not threaded as a prop
  // anymore — TabStack wraps each slot in an `ActiveTabProvider` so pages
  // subscribe directly via `useIsActiveTab()`.
  const renderTab = useCallback(
    (tab: Tab) => <TabContent tab={tab} />,
    [],
  )
  if (tabs.length === 0) return <Overview />
  return (
    <TabStack tabs={tabs} activeTabId={activeTabId}>
      {renderTab}
    </TabStack>
  )
}

const HomeLayout: React.FC = () => {
  return (
    <MainLayout>
      <Suspense fallback={null}>
        <TabStackHost />
      </Suspense>
    </MainLayout>
  )
}

export default HomeLayout